import * as React from 'react';
import { getBaseUrl } from '@/redux/modules/config';
import {
    getItemModelShortDescription,
    getItemModelSlugWithLocation,
    getItemModelTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getItemUrlFromSlug } from '@/utils/urls';
import { getMobileBrowserOS } from '@/redux/modules/browser';
import { getShareText } from './helpers/getShareText';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { recordSocialNetworkShare } from '@/redux/modules/analytics';
import { safelyEscapeText } from '@/utils/safelyRender';
import { UploadSimple } from '@liveauctioneers/hammer-ui-core/icons';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';

const IOS_SHARE_UTM_PARAMS = '?utm_source=LA_Mobile_Share&utm_medium=mobile&utm_campaign=LAiOSshare';
const ANDROID_SHARE_UTM_PARAMS = '?utm_source=LA_Mobile_Share&utm_medium=mobile&utm_campaign=LAiOSshare';

type Props = {
    className?: string;
    itemId: number;
};

const NativeShareItemButton = ({ className, itemId }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const baseUrl = useAppSelector(getBaseUrl);
    const mobileOs = useAppSelector(getMobileBrowserOS);
    const itemTitle = useAppSelector((state) => getItemModelTitle(state, itemId));
    const slug = useAppSelector((state) => getItemModelSlugWithLocation(state, itemId));
    const title = safelyEscapeText(itemTitle.trim());
    const itemPath = getItemUrlFromSlug(itemId, slug);
    let url = `${baseUrl}${itemPath}`;

    if (mobileOs === 'ios') {
        url = `${url}${IOS_SHARE_UTM_PARAMS}`;
    } else {
        url = `${url}${ANDROID_SHARE_UTM_PARAMS}`;
    }

    const description = useAppSelector((state) => getItemModelShortDescription(state, itemId));
    const text = getShareText(title, description, formatMessage({ id: 'socialIcons.nativeShare' }));

    const trackShare = React.useCallback(() => {
        dispatch(recordSocialNetworkShare('native share', url));
    }, [dispatch, url]);

    const navigatorShare = React.useCallback(() => {
        if (navigator.share) {
            navigator
                .share({
                    text,
                    title,
                    url,
                })
                .then(() => {
                    trackShare();
                })
                .catch((error) => console.log('Error sharing', error));
        } else {
            console.log('navigator.share not supported');
        }
    }, [text, title, url, trackShare]);

    return (
        <span
            aria-label="Native Share"
            className={className}
            onClick={navigatorShare}
        >
            <IconButton
                icon={UploadSimple}
                shape="square"
                size="sm"
                variant="outlined"
            />
        </span>
    );
};

export default NativeShareItemButton;
